import { scaleLinear } from 'd3-scale'
import { format, parseISO } from 'date-fns'
import { TooltipButton } from '@/components/ui/TooltipButton'
import type { ChartConfig as ChartConfigV3, ChartHeatmapDataSchema as ChartHeatmapDataSchemaV3 } from './types-v3'
import { useSubmitPageFilters } from '../../AppPage2/Filter/useSubmitPageFilters'
import { Link, useLocation, useSearchParams } from 'react-router'
import { InferFormValues, JsonFormSchemaV2 } from '../FormComponent/types'
import { CATALOG_BASE_PATH } from '@/const'
import { PADDING_GAP, TOTAL_HEADINGS_AND_SPACING } from '../../config'

const defaultMargin = { top: 0, left: 0, right: 0, bottom: 0 }

const DEFAULT_COLOR_DOMAIN: [number, number, number] = [-40, 0, 40]
const DEFAULT_COLOR_RANGE: [string, string, string] = ['#f1872b', '#ffffff', '#2a53a1']

// Group spacing controls
const GROUP_HEADER_HEIGHT = 24
const GROUP_GAP = 30

// Legend spacing controls
const GAP_ROWS_TO_LABEL = 30 // more space between rows and legend label
const GAP_LABEL_TO_BAR = 6 // less space between label and bar
const OUTER_RIGHT_MARGIN = 20 // space between legend and container right edge
const LEGEND_WIDTH = 34

const LEGEND_BOTTOM_BLEED = 14

export type HeatmapV2Props = {
  width: number
  /** Pixel height of the chart tile, used to keep the legend inside the visible area */
  height?: number
  /** Padding above the chart body that is not part of the drawable height */
  topInset?: number
  margin?: { top: number; right: number; bottom: number; left: number }
  data: ChartHeatmapDataSchemaV3
  chartConfig: ChartConfigV3
}

export function ChartBody({
  width,
  height = 0,
  topInset = 0,
  margin = defaultMargin,
  data,
  chartConfig,
}: HeatmapV2Props) {
  const { submitFilters } = useSubmitPageFilters()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const xKey = chartConfig.xAxis.dataKey
  const yKey = chartConfig.yAxis.dataKey
  const zKey = chartConfig.zAxis.dataKey
  const cellAction = chartConfig.cellAction

  const inputGroups = data.data ?? []

  // Build unique x-axis values (columns) in encounter order
  const columnsSet = new Set<string>()
  for (const g of inputGroups) {
    for (const p of g.data as Array<Record<string, unknown>>) {
      const xVal = String((p[xKey] as string | number | Date | undefined) ?? '')
      if (xVal) columnsSet.add(xVal)
    }
  }
  const columns = Array.from(columnsSet)

  // Build groups with rows (each row is a unique y value within the group)
  const groups = inputGroups.map((g) => {
    const yOrder: string[] = []
    const rowKeyByLabel = new Map<string, string>()
    const valueByYX = new Map<string, number>() // key: `${y}__${x}` -> z
    const otherDataByY = new Map<string, Record<string, unknown>>()
    const pointByYX = new Map<string, Record<string, unknown>>() // key: `${y}__${x}` -> original point

    for (const p of g.data as Array<Record<string, unknown>>) {
      const yVal = String((p[yKey] as string | number | Date | undefined) ?? '')
      const xVal = String((p[xKey] as string | number | Date | undefined) ?? '')
      const zValRaw = p[zKey]
      if (!yVal || !xVal) continue
      if (!rowKeyByLabel.has(yVal)) {
        rowKeyByLabel.set(yVal, `${g.key}::${yVal}`)
        yOrder.push(yVal)
        // capture non-axis properties for this row
        const extras: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(p)) {
          if (k !== xKey && k !== yKey && k !== zKey) extras[k] = v as unknown
        }
        otherDataByY.set(yVal, extras)
      }
      const zNum = typeof zValRaw === 'number' ? zValRaw : zValRaw == null ? null : Number(zValRaw)
      if (zNum != null && !Number.isNaN(zNum)) valueByYX.set(`${yVal}__${xVal}`, zNum)
      // keep the original point for cell-level access
      pointByYX.set(`${yVal}__${xVal}`, p as Record<string, unknown>)
    }

    const rows = yOrder.map((label) => {
      const key = rowKeyByLabel.get(label) as string
      const values = columns.map((x) => {
        const v = valueByYX.get(`${label}__${x}`)
        return v == null ? null : v
      })
      const otherData = otherDataByY.get(label) ?? {}
      const points = columns.map((x) => pointByYX.get(`${label}__${x}`) ?? null)
      const cells = columns.map((x, ci) => ({
        columnKey: x,
        columnIndex: ci,
        value: values[ci],
        point: points[ci],
      }))
      return { key, label, values, otherData, points, cells }
    })

    return { key: g.key, title: g.title, rows }
  })

  // compute min/max for color domain if not provided
  let minValue = Infinity
  let maxValue = -Infinity
  for (const g of groups) {
    for (const r of g.rows) {
      for (const v of r.values) {
        if (v === null || v === undefined) continue
        if (v < minValue) minValue = v
        if (v > maxValue) maxValue = v
      }
    }
  }

  const colorDomain =
    chartConfig.zAxis.colorScale?.domain ??
    ([Math.min(minValue, DEFAULT_COLOR_DOMAIN[0]), 0, Math.max(maxValue, DEFAULT_COLOR_DOMAIN[2])] as [
      number,
      number,
      number,
    ])
  const colorRange = chartConfig.zAxis.colorScale?.range ?? DEFAULT_COLOR_RANGE

  const colorScale = scaleLinear<string, string>().domain(colorDomain).range(colorRange)

  // --- dynamic margins based on label sizes ---
  const measureTextWidth = (text: string, font = '12px sans-serif') => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return 0
    ctx.font = font
    const metrics = ctx.measureText(text)
    const inkWidth = metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight
    return inkWidth > 0 ? inkWidth : metrics.width
  }

  // left margin: longest y-label (rows and group titles)
  const fontFamily = typeof document !== 'undefined' ? getComputedStyle(document.body).fontFamily : 'sans-serif'
  const yLabelFont = `12px ${fontFamily}`
  // Group titles render italic and semibold, which are wider than the row labels.
  const groupTitleFont = `italic 600 12px ${fontFamily}`
  const maxYLabelWidth = Math.max(
    0,
    ...groups.flatMap((g) => [
      measureTextWidth(g.title, groupTitleFont),
      ...g.rows.map((r) => measureTextWidth(r.label, yLabelFont)),
    ])
  )
  // keep left padding tight to labels, with room for the italic overhang
  const computedLeft = Math.max(8, Math.ceil(maxYLabelWidth) + 12)

  // top margin: rotated x labels at 45°
  const xFontSize = 10
  const xFont = `${xFontSize}px sans-serif`
  const formatLabel = (c: string) => {
    try {
      return format(typeof c === 'string' ? parseISO(c) : (c as unknown as Date), 'PP')
    } catch {
      return String(c)
    }
  }
  const formattedLabels = columns.map((c) => formatLabel(c))
  const maxXLabelWidth = Math.max(0, ...formattedLabels.map((c) => measureTextWidth(c, xFont)))
  const rotatedHeight = Math.ceil(maxXLabelWidth * Math.sin(Math.PI / 4) + xFontSize * Math.cos(Math.PI / 4))
  // add extra padding so long dates never clip
  // const computedTop = Math.max(32, rotatedHeight + 28)
  const computedTop = rotatedHeight

  // dynamic right margin to ensure legend (label + bar) fits snug against parent right edge
  const legendTickStrings = [colorDomain[0], colorDomain[1], colorDomain[2]].map((v) => String(v))
  const maxLegendTickWidth = Math.max(0, ...legendTickStrings.map((s) => measureTextWidth(s, '11px sans-serif')))
  const legendRectWidth = LEGEND_WIDTH - 24
  const legendBlockWidth = Math.max(legendRectWidth, LEGEND_WIDTH - 20 + 14 + maxLegendTickWidth)
  const labelHalfThickness = xFontSize / 2
  const labelToBarOffset = GAP_LABEL_TO_BAR + labelHalfThickness
  const legendAreaWidth = GAP_ROWS_TO_LABEL + labelToBarOffset + legendBlockWidth
  const computedRight = Math.max(12, Math.ceil(legendAreaWidth) + OUTER_RIGHT_MARGIN)

  const m = { ...margin, top: computedTop, left: computedLeft, right: computedRight }

  // layout
  const gridWidth = Math.max(0, width - m.left - m.right)
  const cellWidth = columns.length > 0 ? gridWidth / columns.length : 0
  const cWidth = Math.max(0, cellWidth - 1)
  const cHeight = Math.min(cWidth, 28)

  const rowsAreaHeight = groups.reduce((sum, g) => sum + g.rows.length * (cHeight + 1), 0)

  // Keep the bar, including the lowest tick, inside the visible chart.
  const gridRowHeightPx =
    typeof window !== 'undefined' ? (window.innerHeight - TOTAL_HEADINGS_AND_SPACING) / 2 - PADDING_GAP / 2 : 300
  const maxLegendHeight = (2 / 3) * gridRowHeightPx
  const legendTop = m.top + GROUP_HEADER_HEIGHT + 1
  const fittedLegendHeight =
    height > legendTop + LEGEND_BOTTOM_BLEED ? height - topInset - legendTop - LEGEND_BOTTOM_BLEED : maxLegendHeight
  const legendHeight = Math.max(0, Math.min(rowsAreaHeight, maxLegendHeight, fittedLegendHeight))

  // Place x-axis just above the grid header; top margin already guarantees clearance
  const axisY = Math.max(0, m.top - 2)

  const xAxisLabel = chartConfig.xAxis.label ?? chartConfig.xAxis.dataKey ?? 'Column'
  const yAxisLabel = chartConfig.yAxis.label ?? chartConfig.yAxis.dataKey ?? 'Row'
  const zAxisLabel = chartConfig.zAxis.label ?? chartConfig.zAxis.dataKey ?? 'Value'
  const tooltipFields = chartConfig.tooltip

  const dateLabels = formattedLabels.map((label, i) => {
    const labelCenter = i * cellWidth + Math.max(0, cellWidth - 1) / 2
    // shift left by half the label height projected along x when rotated 45°,
    // so the label's leading edge aligns with the column center instead of the trailing edge.
    const shift = (xFontSize / 2) * Math.cos(Math.PI / 4) - xFontSize / 2
    return (
      <g key={`x-tick-${i}`} transform={`translate(${labelCenter - shift}, ${axisY}) rotate(-45)`}>
        <text fontSize={10} textAnchor="start" fill="#444">
          {label}
        </text>
      </g>
    )
  })

  return width < 10 ? null : (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="sticky top-0 z-20 bg-background">
          <svg width={width} height={m.top}>
            <g transform={`translate(${m.left}, 0)`}>{dateLabels}</g>
          </svg>
        </div>
        <div className="relative">
          {groups.map((group) => {
            const rowsHeight = group.rows.length * (cHeight + 1)
            return (
              <section key={group.key}>
                <div
                  className="sticky z-10 flex items-center bg-background"
                  style={{ top: m.top, height: GROUP_HEADER_HEIGHT, width: m.left + gridWidth }}
                >
                  <div
                    className="overflow-visible text-right text-xs font-semibold italic leading-none text-[#333]"
                    style={{ width: m.left, paddingRight: 8 }}
                  >
                    {group.title}
                  </div>
                </div>
                <svg width={width} height={rowsHeight}>
                  <g transform={`translate(${m.left}, 0)`}>
                    {group.rows.map((row, ri) => {
                      const y = ri * (cHeight + 1)
                      return (
                        <g key={row.key}>
                          {/* row label */}
                          <text
                            x={-8}
                            y={y + cHeight / 2}
                            textAnchor="end"
                            dominantBaseline="middle"
                            fontSize={12}
                            fill="#333"
                          >
                            {row.label}
                          </text>
                          {/* cells */}
                          {row.cells.map((cell) => {
                            const x = cell.columnIndex * cellWidth
                            const fill = cell.value == null ? '#ffffff' : colorScale(cell.value)

                            const cellElement = (
                              <g>
                                <rect
                                  x={x}
                                  y={y + 1}
                                  width={cWidth}
                                  height={cHeight}
                                  fill={fill}
                                  stroke="#f0f0f0"
                                  strokeWidth={1}
                                  rx={cHeight / 4}
                                  ry={cHeight / 4}
                                ></rect>
                              </g>
                            )

                            // Build filter payload from cellAction.filterMapping using the cell's original data point
                            const buildFilterPayload = (): InferFormValues<JsonFormSchemaV2> => {
                              const payload: Record<string, unknown> = {}
                              const mapping = cellAction?.filterMapping ?? []
                              const point = (cell.point ?? {}) as Record<string, unknown>
                              for (const m of mapping) {
                                const value = point[m.dataKey]
                                if (value !== undefined) {
                                  payload[m.filterId] = value as unknown
                                }
                              }
                              return payload as InferFormValues<JsonFormSchemaV2>
                            }

                            const targetPageId = cellAction?.type === 'navigate' ? cellAction.handlerId : undefined
                            const toHref = `${targetPageId ? `${CATALOG_BASE_PATH}${targetPageId}` : pathname}?${searchParams.toString()}`

                            const tooltipContent = tooltipFields ? (
                              <div className="flex flex-col gap-1 p-1 text-sm font-medium">
                                {tooltipFields.map((field) => {
                                  const point = (cell.point ?? {}) as Record<string, unknown>
                                  const rawValue = point[field.dataKey]
                                  const displayValue =
                                    field.format === 'date' && rawValue != null
                                      ? formatLabel(String(rawValue))
                                      : rawValue != null
                                        ? String(rawValue)
                                        : '–'
                                  return (
                                    <div key={field.dataKey} className="flex items-center gap-3">
                                      <span className="font-light italic">{field.label}:</span> {displayValue}
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1 p-1 text-sm font-medium">
                                <div className="flex items-center gap-3">
                                  <span className="font-light italic">{yAxisLabel}:</span> {row.label}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-light italic">{xAxisLabel}:</span> {formatLabel(cell.columnKey)}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-light italic">{zAxisLabel}:</span> {cell.value}
                                </div>
                              </div>
                            )

                            return (
                              <TooltipButton
                                key={`${row.key}-${cell.columnIndex}`}
                                tooltip={tooltipContent}
                                side="left"
                              >
                                {cellAction?.type === 'navigate' ? (
                                  <Link
                                    to={toHref}
                                    onClick={() => {
                                      const payload = buildFilterPayload()
                                      if (Object.keys(payload).length > 0) {
                                        submitFilters(payload)
                                      }
                                    }}
                                  >
                                    {cellElement}
                                  </Link>
                                ) : (
                                  <g
                                    onClick={() => {
                                      const payload = buildFilterPayload()
                                      if (Object.keys(payload).length > 0) {
                                        submitFilters(payload)
                                      }
                                    }}
                                  >
                                    {cellElement}
                                  </g>
                                )}
                              </TooltipButton>
                            )
                          })}
                        </g>
                      )
                    })}
                  </g>
                </svg>
                <div style={{ height: GROUP_GAP }} />
              </section>
            )
          })}
        </div>
      </div>
      <svg
        className="pointer-events-none absolute z-30"
        style={{ top: m.top }}
        width={width}
        height={GROUP_HEADER_HEIGHT + 1 + legendHeight + LEGEND_BOTTOM_BLEED}
      >
        <defs>
          <linearGradient id="legendGradient" x1="0" x2="0" y1="1" y2="0">
            <stop offset="0%" stopColor={colorRange[0]} />
            <stop offset="50%" stopColor={colorRange[1]} />
            <stop offset="100%" stopColor={colorRange[2]} />
          </linearGradient>
        </defs>
        <g
          transform={`translate(${width - m.right + GAP_ROWS_TO_LABEL + labelToBarOffset}, ${GROUP_HEADER_HEIGHT + 1})`}
        >
          <rect
            width={LEGEND_WIDTH - 24}
            height={legendHeight}
            fill="url(#legendGradient)"
            rx={(LEGEND_WIDTH - 24) / 2}
          />
          {[colorDomain[0], colorDomain[1], colorDomain[2]].map((v, idx) => {
            const pos = ((v - colorDomain[0]) / (colorDomain[2] - colorDomain[0])) * legendHeight
            return (
              <g key={`legend-tick-${idx}`} transform={`translate(${LEGEND_WIDTH - 20}, ${legendHeight - pos})`}>
                <line x1={0} x2={10} y1={0} y2={0} stroke="#777" />
                <text x={14} y={4} fontSize={11} fill="#444">
                  {v}
                </text>
              </g>
            )
          })}
          <g transform={`translate(${-labelToBarOffset}, ${legendHeight / 2}) rotate(-90)`}>
            <text fontSize={12} fontWeight={600} fill="#333" textAnchor="middle" dominantBaseline="middle">
              {zAxisLabel}
            </text>
          </g>
        </g>
      </svg>
    </div>
  )
}
